import React, { useState } from "react";
import { useData } from "@/context/DataContext";
import { PageTitle, EmptyState } from "@/components/shared";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import PhotoGalleryModal from "@/components/PhotoGalleryModal";

export default function Perbandingan() {
  const { data, update } = useData();
  const items = data.vendor || [];
  const [galleryFor, setGalleryFor] = useState(null); // { title, photos }

  const groups = {};
  items.forEach((v) => {
    const key = (v.vendor || "Lainnya").trim();
    (groups[key] = groups[key] || []).push(v);
  });
  const jenisList = Object.keys(groups).sort();

  const choose = async (group, chosen) => {
    await Promise.all(group.map((v) => update("vendor", v.id, { dipakai: v.id === chosen.id })));
    toast.success(`${chosen.nama_vendor} dipilih`);
  };

  const openGallery = (v) => {
    const photos = Array.isArray(v.photos) ? v.photos.filter(Boolean) : [];
    if (photos.length === 0) { toast.info("Belum ada foto vendor"); return; }
    setGalleryFor({ title: `Foto — ${v.nama_vendor}`, photos });
  };

  return (
    <div>
      <PageTitle title="Perbandingan Vendor" subtitle="Bandingkan vendor sejenis & tentukan pilihan" />
      {jenisList.length === 0 && <EmptyState text="Belum ada vendor. Tambah di menu Kontak Vendor." />}
      {jenisList.map((jenis) => {
        const group = groups[jenis];
        const decided = group.some((v) => v.dipakai);
        return (
          <div key={jenis} className="mb-10">
            <h2 className="mb-3 font-heading text-2xl">{jenis}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {group.map((v) => {
                const photos = Array.isArray(v.photos) ? v.photos.filter(Boolean) : [];
                return (
                  <Card key={v.id} data-testid={`compare-${v.id}`} className={`p-5 ${v.dipakai ? "border-2 border-green-600" : ""}`}>
                    <div className="flex items-start justify-between">
                      <h3 className="font-semibold">{v.nama_vendor}</h3>
                      {decided && (v.dipakai
                        ? <Badge className="bg-green-600"><CheckCircle2 className="mr-1 h-3 w-3" />Digunakan</Badge>
                        : <Badge variant="outline" className="text-muted-foreground">Tidak Terpilih</Badge>)}
                    </div>
                    <p className="mt-3 text-2xl font-semibold">{v.quotation ? formatRupiah(v.quotation) : "-"}</p>
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <p>Kontak: {v.nama_kontak || "-"} {v.no_kontak && `· ${v.no_kontak}`}</p>
                      <p>Bank: {v.bank || "-"} {v.no_rekening && `· ${v.no_rekening}`}</p>
                      {v.notes && (
                        <p data-testid={`compare-notes-${v.id}`} className="mt-2 whitespace-pre-line rounded-md border border-dashed border-border bg-muted/40 p-2 text-xs italic text-foreground/80">
                          {v.notes}
                        </p>
                      )}
                    </div>
                    {photos.length > 0 && (
                      <Button
                        data-testid={`view-photos-${v.id}`}
                        onClick={() => openGallery(v)}
                        variant="outline"
                        size="sm"
                        className="mt-3 w-full gap-1.5 rounded-full"
                      >
                        <ImageIcon className="h-3.5 w-3.5" /> Lihat Foto ({photos.length})
                      </Button>
                    )}
                    <Button
                      data-testid={`choose-${v.id}`}
                      onClick={() => choose(group, v)}
                      variant={v.dipakai ? "default" : "outline"}
                      className="mt-2 w-full rounded-full"
                      size="sm"
                    >
                      {v.dipakai ? "Terpilih" : "Pilih vendor ini"}
                    </Button>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      <PhotoGalleryModal
        open={!!galleryFor}
        onOpenChange={(o) => !o && setGalleryFor(null)}
        title={galleryFor?.title}
        photos={galleryFor?.photos || []}
      />
    </div>
  );
}
